import { SubscriptionType, getCurrentUser } from '@editar-pdf/api';
import axios, { isAxiosError } from 'axios';
import { Suspense, lazy, useCallback, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthModal } from './components/AuthModal';
import { FilePickerProvider } from './components/FilePicker/FilePickerProvider';
import MainLayout from './components/Layout/MainLayout/MainLayout';
import EditorLayout from './components/Layout/EditorLayout/EditorLayout';
import Loader from './components/Loader/Loader';
import { PaymentModal } from './components/PaymentModal';
import { Toaster } from './components/ui/sonner';
import { ROUTES, SUPPORTED_LANGUAGES } from './constants/constants';
import { Context, defaultState } from './context/appContext';
import { useDisableBrowserZoom } from './hooks/useDisableBrowserZoom';
import { useGuestId } from './hooks/useGuestId';
import { AppStateActions, appReducerWrapper, type AppStateAction } from './reducers/appReducer';
import { localStorageService } from './services';
import { applyAuthToken } from './services/authToken';
import { errorReporter } from './services/errorReporter';
import { setActionTrackingContext } from './helpers/actionTracker';
import { trackDispatchedAction } from './helpers/actionTracking';
import { useIsMobile } from './hooks/useIsMobile';

const Home = lazy(() => import('./pages/Home'));
const MyFiles = lazy(() => import('./pages/MyFiles'));
const Editor = lazy(() => import('./pages/Editor'));
const Results = lazy(() => import('./pages/Results'));
const Policy = lazy(() => import('./pages/Policy'));
const Refund = lazy(() => import('./pages/Refund'));
const Terms = lazy(() => import('./pages/Terms'));
const Gdpr = lazy(() => import('./pages/Gdpr'));
const Pricing = lazy(() => import('./pages/Pricing/Pricing'));
const WorkInProgress = lazy(() => import('./pages/WorkInProgress/WorkInProgress'));
const Subscribed = lazy(() => import('./pages/Subscribed'));
const Profile = lazy(() => import('./pages/Profile'));
const PasswordRecovery = lazy(() => import('./pages/PasswordRecovery'));
const ConvertToWord = lazy(() => import('./pages/ConvertToWord'));
// Specimen page for the design system. The ternary (not just a guard on the <Route>) is
// what keeps it out of production: Vite replaces `import.meta.env.DEV` with `false`, so
// Rollup drops the branch and never emits the chunk. Guarding only the <Route> would still
// bundle the page.
const DesignSystem = import.meta.env.DEV ? lazy(() => import('./pages/DesignSystem/DesignSystem')) : null;

function App() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  const [state, baseDispatch] = useReducer(appReducerWrapper, defaultState);
  const isMobile = useIsMobile();

  // Action tracking reads pre-action state (page count, current file, scale) to build its
  // payloads, but the dispatch wrapper below is intentionally identity-stable, so state
  // reaches it through a ref rather than a dependency.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    // The mobile/desktop split comes from a media-query hook and so is not reachable
    // from the state layer; hand it to the tracker once instead of per action.
    setActionTrackingContext({ isMobile });
  }, [isMobile]);

  // Wrap dispatch so SET_AUTH_TOKEN's HTTP/persistence side effects run once per dispatch
  // at the app boundary, keeping the reducer pure. This is the single chokepoint for every
  // SET_AUTH_TOKEN dispatcher in the app, so call sites need no changes. In-editor action
  // logging hangs off the same chokepoint, keeping it out of the UI components entirely.
  const dispatch = useCallback((action: AppStateAction) => {
    if (action.type === AppStateActions.SET_AUTH_TOKEN) {
      applyAuthToken(action.payload?.token);
      if (!action.payload?.token) {
        errorReporter.forgetAccount();
      }
    }
    trackDispatchedAction(action, stateRef.current);
    baseDispatch(action);
  }, []);

  const pathLang = pathname.split('/')[1];
  const isSupportedLang = SUPPORTED_LANGUAGES.includes(pathLang);

  const loadUserInfo = useCallback(async () => {
    try {
      const resp = await getCurrentUser();
      if (resp.status === 200) {
        dispatch({
          type: AppStateActions.SET_USER_INFO,
          payload: { userInfo: resp.data },
        });
        errorReporter.identifyAccount({ id: resp.data.id, subscriptionType: resp.data.subscriptionType });
        if (resp.data.subscriptionType === SubscriptionType.FREE && state.authToken?.source !== 'AUTO') {
          dispatch({ type: AppStateActions.SET_NEED_PAYMENT, payload: { needPayment: true } });
        }
      }
    } catch (_ignore) {
      //nothing to do
    }
  }, [state.authToken?.source, dispatch]);

  useGuestId();
  useDisableBrowserZoom();

  useEffect(() => {
    const sessionAuthToken = localStorageService.getAuthToken();
    if (sessionAuthToken) {
      dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: { token: sessionAuthToken, source: 'AUTO' } });
    }

    axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (
          err &&
          isAxiosError(err) &&
          (err.status === 401 || err.response?.data.message?.includes?.('401 JWT expired')) // TODO: remove message case
        ) {
          dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: undefined });
        }
        return Promise.reject(err);
      },
    );
  }, [dispatch]);

  useEffect(() => {
    if (isSupportedLang) {
      i18n.changeLanguage(pathLang);
      document.documentElement.lang = pathLang;
    }
  }, [i18n, isSupportedLang, pathLang]);

  useEffect(() => {
    if (state.authToken) {
      void loadUserInfo();
    }
  }, [loadUserInfo, state.authToken]);

  useEffect(() => {
    document.addEventListener('resume', () => location.reload());
  }, []);

  if (!isSupportedLang) {
    return <Navigate to={'/en'} />;
  }

  return (
    <Context.Provider value={{ state, dispatch }}>
      <FilePickerProvider>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route element={<MainLayout />}>
              <Route path={ROUTES.home(':lang')} element={<Home />} />
              <Route path={ROUTES.myFiles(':lang')} element={<MyFiles />} />
              <Route path={ROUTES.restorePassword(':lang')} element={<PasswordRecovery />} />
              <Route path={ROUTES.results(':lang', ':documentId')} element={<Results />} />
              <Route path={ROUTES.pricing(':lang')} element={<Pricing />} />
              <Route path={ROUTES.workInProgress(':lang')} element={<WorkInProgress />} />
              <Route path={ROUTES.policy(':lang')} element={<Policy />} />
              <Route path={ROUTES.refund(':lang')} element={<Refund />} />
              <Route path={ROUTES.terms(':lang')} element={<Terms />} />
              <Route path={ROUTES.gdpr(':lang')} element={<Gdpr />} />
              <Route path={ROUTES.subscribed(':lang')} element={<Subscribed />} />
              <Route path={ROUTES.convertToWord(':lang')} element={<ConvertToWord />} />
              <Route path={ROUTES.profile(':lang', '*')} element={<Profile />} />
            </Route>
            {/* Deliberately outside MainLayout: this page is a specimen sheet, and the site
                chrome would only get in the way of judging the primitives on their own. */}
            {DesignSystem && <Route path={ROUTES.designSystem(':lang')} element={<DesignSystem />} />}
            <Route element={<EditorLayout />}>
              <Route path={ROUTES.edit(':lang', ':documentId')} element={<Editor />} />
            </Route>
            <Route path="*" element={<Navigate to={ROUTES.home(i18n.language)} />} />
          </Routes>
        </Suspense>
      </FilePickerProvider>
      <Toaster position="bottom-right" />
      <AuthModal />
      <PaymentModal />
    </Context.Provider>
  );
}

export default App;
